/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { compose } from 'recompose';
import { connect, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { object, string } from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';
import useIsPlaPage from '../../../../../../helpers/hooks/useIsPlaPage';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsDisplayChannel from '../../../../../../helpers/hooks/useIsDisplayChannel';
import useSalesforcePersonalizedContentQuery from '../../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getBrandName } from '../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';

/**
 * ----------------ThreeColumn Layout XL & L-------------------
 * |                                                          |
 * |  |---contentLeft---| |------contentRightContainer------| |
 * |  |                 | |                                 | |
 * |  |  width: 32.5%   | |         width: 66%              | |
 * |  |                 | |                                 | |
 * |  |                 | | |contentMiddle | |contentRight| | |
 * |  |                 | | |              | |            | | |
 * |  |                 | | |  width: 49%  | | width: 49% | | |
 * |  |                 | | |              | |            | | |
 * |  |                 | | |              | |            | | |
 * |  |                 | | |              | |            | | |
 * |  |                 | | |              | |            | | |
 * |  |-----------------| | |--------------| |------------| | |
 * |                      |---------------------------------| |
 * ------------------------------------------------------------
 */

/**
 * ----------------ThreeColumn Layout M -----------------------
 * |                                                          |
 * |  |---contentLeft---| |------contentRightContainer------| |
 * |  |                 | |                                 | |
 * |  |  width: 39%     | |         width: 59%              | |
 * |  |                 | |                                 | |
 * |  |                 | | |--------contentMiddle--------| | |
 * |  |                 | | |                             | | |
 * |  |                 | | |        width: 100%          | | |
 * |  |                 | | |-----------------------------| | |
 * |  |                 | |                                 | |
 * |  |                 | | |--------contentRight --------| | |
 * |  |                 | | |                             | | |
 * |  |                 | | |        width: 100%          | | |
 * |  |                 | | |-----------------------------| | |
 * |  |-----------------| |---------------------------------| |
 * ------------------------------------------------------------
 */

/**
 * ----------------ThreeColumn Layout 650px--------------------
 * |                                                          |
 * |  |-------------------contentLeft-----------------------| |
 * |  |                                                     | |
 * |  |                  width: 100%                        | |
 * |  |-----------------------------------------------------| |
 * |                                                          |
 * |  |------------contentRightContainer--------------------| |
 * |  |                                                     | |
 * |  |                  width: 100%                        | |
 * |  | |--------------contentMiddle---------------------|  | |
 * |  | |                                                |  | |
 * |  | |                 width: 100%                    |  | |
 * |  | |------------------------------------------------|  | |
 * |  |                                                     | |
 * |  | |--------------contentRight----------------------|  | |
 * |  | |                                                |  | |
 * |  | |                 width: 100%                    |  | |
 * |  | |------------------------------------------------|  | |
 * |  |-----------------------------------------------------| |
 * ------------------------------------------------------------
 */

const useStyles = makeStyles((theme) => ({
    contentTop: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        margin: '0px auto',
        maxWidth: '1380px',
        color: theme.palette.black,

        [theme.breakpoints.down('650')]: {
            flexDirection: 'column',

            width: '100%',
        },
    },

    contentLeft: {
        width: '32.5%',
        '@media screen and (max-width: 1279.95px) and (min-width: 1026px)': {
            width: '295px',
        },
        '@media screen and (max-width: 1026px)': {
            width: '39%',
        },

        [theme.breakpoints.down('650')]: {
            width: '100%',
        },
    },

    contentRightContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'no-wrap',

        width: '66%',
        '@media screen and (max-width: 1279.95px) and (min-width: 1026px)': {
            width: 'calc(100% - 310px)',
        },
        '@media screen and (max-width: 1026px)': {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: '59%',
        },

        [theme.breakpoints.down('650')]: {
            width: '100%',
        },
    },

    contentMiddle: {
        width: '49%',
        '@media screen and (max-width: 1026px)': {
            width: '100%',
        },
    },

    contentRight: {
        width: '49%',
        '@media screen and (max-width: 1026px)': {
            width: '100%',
        },
        '& [id="passport-banner-section"]': {
            marginBottom: '20px',
        },
    },
}));

// documentation: https://1800flowersinc.atlassian.net/wiki/spaces/RP/pages/288227337/PDP+Product+Details+Page
const ThreeColumn = (props) => {
    const {
        ssrDeviceType,
        components,
        product,
        isPassportAfterCta,
    } = props;
    const oneSku = product?.upsells?.length === 0 && product?.productSkus?.length === 1;
    const {
        ProductImageUI,
        SmartGift,
        ProductTitle,
        ProductReview,
        GraphqlProductSkus,
        GraphqlAddonContainer,
        GCIPersonalization,
        SnipeBanner,
        GraphqlAgeVerification,
        SubscriptionProductBannerUI,
        ProductQuantity,
        GraphqlPDPPromoContainer,
        PmallSummary,
        PmallPersonalization,
        PDPAddToCart,
        PDPDeliveryPopupContainer,
        PromoMessageUI,
        ProductDetails,
        PLAProductIcon,
        PassportBannerUI,
        QandASection,
    } = components;

    const classes = useStyles();
    const isPlaPage = useIsPlaPage();
    const isDisplayChannel = useIsDisplayChannel();
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));
    const isPlaPDPPassportEnabled = useSelector(getFeatureFlag('is-pla-pdp-passport-enabled'));
    const plaThreeColumn = (isPlaPage || isDisplayChannel) && foodPlaRedesign === 'three_column';
    const brandName = useSelector(getBrandName);
    const { data: passportSaleforceData } = useSalesforcePersonalizedContentQuery({
        queryName: 'passportModuleOnPla',
        interactionName: `${brandName} - Get Campaign - Passport Module On PLA PDP `,
        featureFlag: isPlaPDPPassportEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const passportAbTest = passportSaleforceData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup || 'Control';
    useClickStreamCustomEventsTracking({
        salesforceResponse: passportSaleforceData?.contentSF,
        isFireImpression: true,
        page: { type: 'product' },
    });
    const showPassport = passportAbTest?.toLowerCase() === 'control' && plaThreeColumn && isPlaPDPPassportEnabled;

    // For ProductReview
    const isPdpReviewsEnabled = useSelector(getFeatureFlag('is-pdp-reviews-enabled'));
    const isPowerReviewEnabled = useSelector(getFeatureFlag(['is-power-review-enabled']));

    // uncomment when needed
    const variantName = 'ThreeColumn'; // this should be unique and never change

    const desktopLayout = () => (
        <div
            className={classes.contentTop}
            data-testid="pdp-variant-threecolumn"
        >
            <div className={classes.contentLeft}>
                {ProductImageUI()}
            </div>
            <div className={classes.contentRightContainer}>

                <div className={classes.contentMiddle}>
                    {ProductTitle()}
                    {isPdpReviewsEnabled && isPowerReviewEnabled && ProductReview()}
                    {GraphqlProductSkus()}
                    {!plaThreeColumn && GraphqlAddonContainer()}
                    {GCIPersonalization()}
                    {SnipeBanner()}
                    {GraphqlAgeVerification()}
                    {!plaThreeColumn && SubscriptionProductBannerUI()}
                    {PmallSummary()}
                    {ProductQuantity()}
                    {GraphqlPDPPromoContainer()}
                    {PmallPersonalization()}
                    {plaThreeColumn && oneSku && ReactHtmlParser(product.shortDescription)}
                </div>

                <div className={classes.contentRight}>
                    {plaThreeColumn && ProductDetails('', plaThreeColumn && oneSku, false, true)}
                    {showPassport && !isPassportAfterCta && PassportBannerUI()}
                    {PDPAddToCart()}
                    {showPassport && isPassportAfterCta && PassportBannerUI()}
                    {PDPDeliveryPopupContainer()}
                    {PromoMessageUI()}
                    {SmartGift(variantName)}
                    {QandASection()}
                </div>

            </div>
        </div>
    );

    const mobileLayout = () => (
        <>
            {PLAProductIcon()}
            <div
                className={classes.contentTop}
                data-testid="pdp-variant-threecolumn"
            >
                <div className={classes.contentLeft}>
                    {ProductImageUI()}

                </div>
                <div className={classes.contentRightContainer}>

                    <div className={classes.contentMiddle}>
                        {ProductTitle()}
                        {isPdpReviewsEnabled && isPowerReviewEnabled && ProductReview()}
                        {GraphqlProductSkus()}
                        {GraphqlAddonContainer()}
                        {GCIPersonalization()}
                        {SnipeBanner()}
                        {GraphqlAgeVerification()}
                        {!plaThreeColumn && SubscriptionProductBannerUI()}
                        {PmallSummary()}
                        {ProductQuantity()}
                        {GraphqlPDPPromoContainer()}
                        {PmallPersonalization()}
                    </div>

                    <div className={classes.contentRight}>
                        {showPassport && !isPassportAfterCta && PassportBannerUI()}
                        {PDPAddToCart()}
                        {showPassport && isPassportAfterCta && PassportBannerUI()}
                        {plaThreeColumn && ProductDetails('', plaThreeColumn && oneSku, false, true)}
                        {PDPDeliveryPopupContainer()}
                        {PromoMessageUI()}
                        {SmartGift()}
                        {QandASection()}
                    </div>

                </div>
            </div>
        </>
    );

    if (ssrDeviceType === 'mobile') {
        return mobileLayout();
    }

    return desktopLayout();
};

const mapStateToProps = (state) => ({
    ssrDeviceType: getSSRDeviceType(state),
});

ThreeColumn.propTypes = {
    ssrDeviceType: string.isRequired,
    components: object.isRequired,
    product: object.isRequired,
};

const enhance = compose(
    connect(mapStateToProps),
);

export default enhance(ThreeColumn);
